import type { DrillDownNode, DrillDownPath } from './types';

// Matches a path (array of segments) to a DrillDownPath<T> using the drillDown tree.
export function matchDrillDownPath<T>(
  tree: DrillDownNode<T>[],
  segments: string[],
  consumedSegments: string[] = [],
  prevParams: Record<string, string> = {},
): DrillDownPath<T> {
  for (const node of tree) {
    const segParts = node.segment.split('/').filter(Boolean);

    if (
      (!node.prefix || node.prefix === consumedSegments.at(-1)) &&
      segParts.every(
        (part, i) =>
          part.startsWith(':?') || // is optional
          (segments[i] && (part.startsWith(':') || segments[i] === part)), // or can consume a segment
      )
    ) {
      const requiredCount = segParts.filter((s) => !s.startsWith(':?')).length;
      const hasOptional = requiredCount < segParts.length;

      // When the node has optional params AND children, try matching
      // children after consuming only the required segments, then with one
      // optional more, and so on (fewest optionals first). This prevents
      // optional params from greedily consuming child route literals
      // (e.g. ':checklistId/:?questionId' swallowing 'activity') and lets a
      // node with several optionals (':?date/:?techId') drill into a child
      // when only some of them are present in the URL.
      // The "all optionals consumed" case is handled by the regular match
      // below, which is where prefix-based children (tabs) are resolved.
      if (hasOptional && node.children) {
        for (let count = requiredCount; count < segParts.length; count++) {
          const remainder = segments.slice(count);
          if (remainder.length === 0) break;

          const baseConsumed = [
            ...consumedSegments,
            ...segments.slice(0, count),
          ];
          const baseParams = segParts
            .slice(0, count)
            .reduce<Record<string, string>>(
              (params, part, i) => {
                if (part.startsWith(':?')) {
                  params[part.slice(2)] = segments[i];
                } else if (part.startsWith(':')) {
                  params[part.slice(1)] = segments[i];
                }
                return params;
              },
              { ...prevParams },
            );

          const childMatch = matchDrillDownPath(
            node.children,
            remainder,
            baseConsumed,
            baseParams,
          );

          if (childMatch.length > 0) {
            const partialMatch = {
              node,
              base:
                '/' +
                [...consumedSegments, ...segments.slice(0, requiredCount)].join(
                  '/',
                ),
              path: '/' + baseConsumed.join('/'),
              params: baseParams,
            };
            return [partialMatch, ...childMatch];
          }
        }
      }

      const match = {
        node,
        base:
          '/' +
          [...consumedSegments, ...segments.slice(0, requiredCount)].join('/'),
        path:
          '/' +
          [...consumedSegments, ...segments.slice(0, segParts.length)].join(
            '/',
          ),
        params: segParts.reduce<Record<string, string>>(
          (params, part, i) => {
            // we've already confirmed that the segments match, we only need to create the params object at this point
            if (part.startsWith(':?')) {
              params[part.slice(2)] = segments[i];
            } else if (part.startsWith(':')) {
              params[part.slice(1)] = segments[i];
            }
            return params;
          },
          { ...prevParams },
        ),
      };

      const remainder = segments.slice(segParts.length);
      if (remainder.length) {
        if (node.children) {
          const newlyConsumedSegments = [
            ...consumedSegments,
            ...segments.slice(0, segParts.length),
          ];
          return [
            match,
            ...matchDrillDownPath(
              node.children,
              remainder,
              newlyConsumedSegments,
              match.params,
            ),
          ];
        } else {
          return [match];
        }
      } else {
        return [match];
      }
    }
  }
  return [];
}
